import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';
import { SimChart } from './SimChart';
import { OrderTicket } from './OrderTicket';
import { PositionsPanel } from './PositionsPanel';
import { NotificationRail } from './NotificationRail';
import { TradeHeader } from './TradeHeader';
import { ChartToolbar } from './ChartToolbar';
import { buildLevels, roundTo } from './simMath';
import { clearRedirect } from './authRedirect';
import './sim.css';

const API = `${BACKEND_URL}/api`;

const pickDeepLink = (list, params) => {
  const n = params.get('n');
  const pair = params.get('pair');
  const dir = params.get('dir');
  if (n) { const hit = list.find((x) => x.id === n); if (hit) return hit; }
  if (!pair) return null;
  const same = list.filter((x) => x.pair === pair && (!dir || x.direction === dir));
  const ts = params.get('ts') ? new Date(params.get('ts')).getTime() : Date.now();
  return same.sort((a, b) => Math.abs(new Date(a.created_at) - ts) - Math.abs(new Date(b.created_at) - ts))[0] || null;
};

// Toast when the engine fills a pending order or closes a live trade at SL/TP.
const announceChanges = (seen, data, setTab) => {
  const now = {};
  ['pending', 'live', 'closed'].forEach((k) => data[k].forEach((o) => { now[o.order_id] = o.status; }));
  const prev = seen.current;
  seen.current = now;
  if (!prev) return;
  data.live.forEach((o) => {
    if (prev[o.order_id] === 'PENDING') { toast.success(`${o.pair} ${o.direction} is now LIVE at ${o.entry_price}`); setTab('live'); }
  });
  data.closed.forEach((o) => {
    if (o.status !== 'CLOSED' || !prev[o.order_id] || prev[o.order_id] === 'CLOSED') return;
    const pnl = o.realized_pnl || 0;
    const what = o.close_reason === 'TP' ? 'hit Take Profit' : o.close_reason === 'SL' ? (o.sl_trailed ? 'hit its trailed Stop Loss' : 'hit Stop Loss') : 'closed';
    (pnl >= 0 ? toast.success : toast.error)(`${o.pair} ${what} — ${pnl >= 0 ? '+' : '-'}$${Math.abs(pnl).toFixed(2)}`);
    setTab('closed');
  });
};

export default function TradeScreen({ onLogout, isAdmin }) {
  const [params] = useSearchParams();
  const [pair, setPair] = useState(params.get('pair') || 'EURUSD');
  const [direction, setDirection] = useState(params.get('dir') === 'SELL' ? 'SELL' : 'BUY');
  const [tf, setTf] = useState('5m');
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState(null);
  const [levels, setLevels] = useState(null);
  const [needsLevels, setNeedsLevels] = useState(true);
  const [setup, setSetup] = useState(null);
  const [risk, setRiskState] = useState(localStorage.getItem('sim_risk') || '50');
  const [notifs, setNotifs] = useState([]);
  const [orders, setOrders] = useState({ pending: [], live: [], closed: [] });
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState('pending');
  const resolved = useRef(false);
  const seen = useRef(null);
  const chartRef = useRef(null);

  const setRisk = (v) => { setRiskState(v); localStorage.setItem('sim_risk', v); };

  const loadQuote = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sim/quote/${pair}`, { params: { tf } });
      setQuote(data);
      setQuoteErr(null);
    } catch (e) {
      setQuoteErr(e.response?.data?.detail || 'Live prices are temporarily unavailable');
    }
  }, [pair, tf]);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sim/orders`);
      announceChanges(seen, data, setTab);
      setOrders(data);
    } catch (e) { /* keep last */ }
  }, []);

  const tradeNotification = useCallback((n) => {
    setPair(n.pair);
    setDirection(n.direction === 'SELL' ? 'SELL' : 'BUY');
    setSetup(n);
    setNeedsLevels(true);
    chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => { clearRedirect(); }, []);

  useEffect(() => {
    setQuote((q) => (q && q.pair === pair ? q : null));
    loadQuote();
    const t = setInterval(loadQuote, 10000);
    return () => clearInterval(t);
  }, [loadQuote, pair]);

  const hasOpen = orders.pending.length + orders.live.length > 0;
  useEffect(() => { loadOrders(); const t = setInterval(loadOrders, hasOpen ? 5000 : 15000); return () => clearInterval(t); }, [loadOrders, hasOpen]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/user/v4-alerts`);
        const list = data.notifications || [];
        setNotifs(list);
        if (!resolved.current) {
          resolved.current = true;
          const hit = pickDeepLink(list, params);
          if (hit) tradeNotification(hit);
        }
      } catch (e) { /* rail stays empty */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [params, tradeNotification]);

  useEffect(() => {
    if (!needsLevels || !quote || quote.pair !== pair) return;
    const fromSetup = setup && setup.pair === pair ? setup : {};
    setLevels(buildLevels(direction, quote.price, pair, quote.digits, fromSetup));
    setNeedsLevels(false);
  }, [needsLevels, quote, pair, direction, setup]);

  const onDrag = useCallback((k, p) => {
    setLevels((l) => (l ? { ...l, [k]: roundTo(p, quote?.digits || 5) } : l));
  }, [quote?.digits]);

  const changePair = (p) => { setPair(p); setSetup(null); setNeedsLevels(true); };
  const changeDirection = (d) => { if (d !== direction) { setDirection(d); setNeedsLevels(true); } };

  const activate = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/sim/orders`, {
        pair, direction, entry_price: levels.entry, sl_price: levels.sl, tp_price: levels.tp,
        risk_amount: Number(risk), notification_id: setup?.pair === pair ? setup.id : null,
        scanner: setup?.pair === pair ? setup.scanner : null,
      });
      toast.success(`${data.order.order_type.replace('_', ' ')} placed — ${data.order.lot_size} lots on ${pair}`);
      loadOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not place the order');
    } finally { setSubmitting(false); }
  };

  const cancel = async (o) => {
    setBusy(o.order_id);
    try { await axios.post(`${API}/sim/orders/${o.order_id}/cancel`); toast.success(`${o.pair} order cancelled`); loadOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
    finally { setBusy(null); }
  };

  const closeTrade = async (o) => {
    setBusy(o.order_id);
    try {
      const { data } = await axios.post(`${API}/sim/orders/${o.order_id}/close`);
      const pnl = data.order.realized_pnl;
      toast.success(`${o.pair} closed at ${data.order.exit_price} — ${pnl >= 0 ? '+' : '-'}$${Math.abs(pnl).toFixed(2)}`);
      seen.current = { ...seen.current, [o.order_id]: 'CLOSED' };
      setTab('closed');
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not close the trade'); }
    finally { setBusy(null); }
  };

  const setAutoTrail = async (o, enabled) => {
    setBusy(o.order_id);
    try {
      await axios.post(`${API}/sim/orders/${o.order_id}/auto-trail`, { enabled });
      toast.success(`${o.pair} Auto-trail ${enabled ? 'ON — SL locks +1R at 1.5R' : 'OFF — you can now move the SL'}`);
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change Auto-trail'); }
    finally { setBusy(null); }
  };

  const moveSl = async (o, sl) => {
    setBusy(o.order_id);
    try {
      const { data } = await axios.post(`${API}/sim/orders/${o.order_id}/sl`, { sl_price: sl });
      toast.success(`${o.pair} SL moved to ${data.order.sl_price}`);
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not move the Stop Loss'); }
    finally { setBusy(null); }
  };

  return (
    <div className="sim-root" data-testid="trade-screen">
      <TradeHeader onLogout={onLogout} isAdmin={isAdmin} />
      <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-6 sm:px-8 sm:py-8">
        <NotificationRail notifications={notifs} selectedId={setup?.pair === pair ? setup.id : null} onTrade={tradeNotification} />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_390px]">
          <div ref={chartRef} className="sim-panel overflow-hidden lg:col-start-1 lg:row-start-1">
            <ChartToolbar pair={pair} onPair={changePair} tf={tf} onTf={setTf} quote={quote} setup={setup?.pair === pair ? setup : null} />
            <div className="h-[360px] sm:h-[460px] lg:h-[540px]">
              <SimChart candles={quote?.candles} digits={quote?.digits || 5} levels={levels} onDrag={onDrag} chartKey={`${pair}-${tf}`} />
            </div>
            <div className="border-t border-white/5 px-4 py-2.5 text-xs text-slate-500" data-testid="chart-hint">
              {quoteErr ? <span className="text-rose-300">{quoteErr}</span> : 'Drag the ENTRY, SL and TP lines on the chart — or use the sliders.'}
            </div>
          </div>
          <div className="lg:sticky lg:top-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start">
            <OrderTicket pair={pair} direction={direction} onDirection={changeDirection} quote={quote?.pair === pair ? quote : null}
              levels={levels} setLevels={setLevels} risk={risk} setRisk={setRisk} onActivate={activate} submitting={submitting} />
          </div>
          <div className="lg:col-start-1 lg:row-start-2">
            <PositionsPanel orders={orders} tab={tab} onTab={setTab}
              onCancel={cancel} onClose={closeTrade} onAuto={setAutoTrail} onMoveSl={moveSl} busy={busy} />
          </div>
        </div>
      </main>
    </div>
  );
}
